<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import Flatpickr from 'svelte-flatpickr'
    import { writable } from 'svelte/store'
    import { slide } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'
    import { marked } from 'marked'

    import { gettext as _, resolve, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { DateTime, toDate, toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import { paymentMethodText, statusText, type TOrderStatus, type TPaymentMethod } from './Order/order'

    import DateComponent from 'hydria/components/Date.svelte'
    import PageList from 'hydria/components/PageList.svelte'

    const LIMIT = 10
    const DELAY_MS = 1250

    const STATUSES = ['final', 'paid', 'processing', 'unpaid', 'quotation', 'ready', 'draft', 'deleted'] as TOrderStatus[]

    // What to search for when we have no status selected
    const NO_STATUSES = ['final', 'paid', 'processing', 'unpaid', 'quotation', 'ready', 'draft'] as TOrderStatus[]
    const TRANSIENT_STATUSES = new Set(['processing'])
    const STATUSES_WITH_PAYMENTS = new Set(['final', 'paid', 'deleted'])
    const METHODS = ['credit card', 'stripe', 'cheque', 'wire transfer', 'internal'] as TPaymentMethod[]

    type Order = '' | 'ctime'

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
        skip: 'int1+',
        s_by: 'string',
        s_desc: 'boolean',
        status: 'string',
        payment_method: 'string',
        date_lower: 'string',
        date_upper: 'string',
        date_is_mtime: 'boolean',
    }
    const mappingOrder = ['q', 'skip']

    // Search options for the backend
    interface SOptions {
        q: string
        skip: number
        s_by: string
        status: TOrderStatus | ''
        payment_method: TPaymentMethod | ''
        date_lower: string
        date_upper: string
        date_is_mtime: boolean
    }
    const defaults: SOptions = {
        q: '',
        skip: 0,
        s_by: '',
        status: '',
        payment_method: '',
        date_lower: '',
        date_upper: '',
        date_is_mtime: false,
    }

    // Search result from the server
    interface ISearchResult {
        items: Array<any>
        total_items: number
        user_name_mapping: {
            [key: string]: {
                name?: string
                org?: string
            }
        }
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    // Purpose: set initial value on Flatpickr
    let dateUpper: Date | undefined = $state()
    let dateLower: Date | undefined = $state()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        if (options.skip && options.skip == loadedOptions.skip) {
            // Something non-skip must have changed, so we force skip to 0
            options.skip = 0
        }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/ecom/orders' : `/ecom/orders?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/ecom/orders' : `/ecom/orders?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    let results = writable([] as any[])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = {}
        if (sOptions.q != '') sdata.text = sOptions.q
        for (const [field, value] of Object.entries(sOptions)) {
            if (['q', 'skip', 's_by', 's_desc', 'date_is_mtime'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (value != null && value != '') sdata[field] = value
                    break
            }
        }

        if (!sdata['status']) sdata['status'] = [...NO_STATUSES]

        if (sOptions.skip > 0) sdata.skip = sOptions.skip
        sdata.limit = LIMIT
        sdata.sort_by = sOptions.s_by == '' ? 'mtime' : sOptions.s_by
        sdata.date_is_mtime = sOptions.date_is_mtime ? true : false

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/ecom/1/orders', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = $state(0)
    let nameMapping: { [key: string]: string } = $state({})

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []

        nItems = data.total_items

        for (const item of data.items) {
            for (const key of ['ctime', 'mtime']) {
                if (item[key]) item[key] = toLuxon(item[key])
            }
            litems.push(item)
        }
        nameMapping = {}
        for (const [id, mapping] of Object.entries(data.user_name_mapping)) {
            let name: string
            if (mapping.name && mapping.org) name = `${mapping.name} (${mapping.org})`
            else if (mapping.name) name = mapping.name
            else if (mapping.org) name = `? (${mapping.org})`
            else continue
            nameMapping[id] = name
        }

        results.set(litems)
        if (loading) loading = false
    }

    function toggleSort(type: Order) {
        if (sOptions.s_by == type) return
        sOptions = { ...sOptions, s_by: type }
        setQueryString(sOptions)
    }

    // svelte-ignore state_referenced_locally
    let text = writable(sOptions.q)

    function loadIntoForm() {
        $text = sOptions.q
        if (sOptions.date_lower) {
            dateLower = toDate(sOptions.date_lower)
        }
        if (sOptions.date_upper) {
            dateUpper = toDate(sOptions.date_upper)
        }
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue == sOptions.q) {
                return
            }
            textTimer = setTimeout(() => {
                sOptions.q = newValue
                textTimer = null
            }, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })

    const flatOpts_lower: any = {
        element: '#date_lower',
    }
    const flatOpts_upper: any = {
        element: '#date_upper',
    }

    let dropdownStatusOpen = $state(false)
    let dropdownMethodOpen = $state(false)

    let warnHtml: string | Promise<string> | undefined = $derived.by(() => {
        if (sOptions.payment_method && sOptions.status && !STATUSES_WITH_PAYMENTS.has(sOptions.status)) {
            return marked.parse($_('Avec un _mode de paiement_, seuls les statuts suivants produisent un résultat : **{statuses}**.', { statuses: [...STATUSES_WITH_PAYMENTS].join(', ') }))
        } else {
            return undefined
        }
    })

    function sameDate(a: DateTime, b: DateTime) {
        return parseInt(a.toFormat('yyyyMMdd'), 10) == parseInt(b.toFormat('yyyyMMdd'), 10)
    }
</script>

<style>
    .ecom-list-filters {
        display: grid;
        grid-template-columns: 3fr repeat(2, minmax(10em, 1fr)) 7.75em repeat(2, minmax(10em, 1fr));
        grid-template-rows: auto;
        grid-gap: 0 1rem;
    }
    .elf-dateis button {
        width: 100%;
    }
    .elf-warning {
        grid-row: 2;
        grid-column: 1 / -1;
        color: var(--bs-warning);
        font-size: 0.9em;
        align-self: center;
        justify-self: center;
        line-height: 1rem;
        text-align: center;

        padding-top: 1rem;
    }

    thead > tr {
        border-bottom: 2px solid #f3f5f6;
    }
    th {
        border-bottom: none;
    }
    tr {
        display: grid;
        grid-template-columns: 2fr 1fr 2.5em 7em 12em;
        grid-template-rows: auto;
        /* gap: 0.5em; */
    }
    td:not(:first-child),
    th:not(:first-child) {
        padding-left: 0.25rem;
    }
    td:not(:last-child),
    th:not(:last-child) {
        padding-right: 0.25rem;
    }
    tr > *:nth-child(-n + 2) {
        overflow: hidden;
        text-overflow: ellipsis;
    }
    tr > *:nth-child(3) {
        text-align: center;
    }
    tr > *:nth-child(4) {
        text-align: center;
    }
</style>

<svelte:body
    onclick={(e) => {
        if (dropdownStatusOpen) dropdownStatusOpen = false
        if (dropdownMethodOpen) dropdownMethodOpen = false
    }}
/>

<Breadcrumb crumbs={[{ txt: tgettext('Commandes') }]} />

<div class="card mb-4">
    <div class="card-title text-primary">{$_('Paramètres de recherche')}</div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
                submit()
            }}
        >
            <div class="ecom-list-filters">
                {#if warnHtml}
                    <div class="elf-warning" transition:slide={{ duration: 200 }}>
                        <div>{@html warnHtml}</div>
                    </div>
                {/if}

                <div class="elf-text">
                    <div class="input-group">
                        <input type="text" class="form-control" placeholder={$_('Filtrer par texte…')} bind:this={mainInput} bind:value={$text} />
                        <button class="btn btn-primary" type="submit"><Icon inline icon="fa:search" /></button>
                    </div>
                </div>

                <div class="elf-date">
                    <Flatpickr
                        options={flatOpts_lower}
                        formattedValue={sOptions.date_lower}
                        value={dateLower}
                        on:valueUpdate={(e) => {
                            sOptions.date_lower = e.detail[1]
                            setQueryString(sOptions)
                        }}
                        dateFormat="Y-m-d"
                        element="#date_lower"
                    >
                        <div id="date_lower" class="input-group">
                            <input type="text" class="form-control" data-input placeholder="date min" />
                            <button
                                class="btn btn-secondary"
                                type="button"
                                onclick={() => {
                                    sOptions.date_lower = ''
                                    setQueryString(sOptions)
                                }}
                                data-clear
                                disabled={!sOptions.date_lower}><Icon inline icon="fa:times" /></button
                            >
                        </div>
                    </Flatpickr>
                </div>
                <div class="elf-date">
                    <Flatpickr
                        options={flatOpts_upper}
                        value={dateUpper}
                        formattedValue={sOptions.date_lower}
                        on:valueUpdate={(e) => {
                            sOptions.date_upper = e.detail[1]
                            setQueryString(sOptions)
                        }}
                        dateFormat="Y-m-d"
                        element="#date_upper"
                    >
                        <div id="date_upper" class="input-group">
                            <input type="text" class="form-control" data-input placeholder="date max" />
                            <button
                                class="btn btn-secondary"
                                type="button"
                                data-clear
                                onclick={() => {
                                    sOptions.date_upper = ''
                                    setQueryString(sOptions)
                                }}
                                disabled={!sOptions.date_upper}><Icon inline icon="fa:times" /></button
                            >
                        </div>
                    </Flatpickr>
                </div>
                <div class="elf-dateis">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        onclick={(e) => {
                            sOptions.date_is_mtime = !sOptions.date_is_mtime
                            setQueryString(sOptions)
                        }}
                    >
                        ←
                        {#if sOptions.date_is_mtime}M-à-j
                        {:else}Création
                        {/if}
                    </button>
                </div>

                <div class="elf-status">
                    <div class="dropdown dropdown-selstyle dropdown-block">
                        <button
                            type="button"
                            class="btn btn-outline-secondary dropdown-toggle"
                            onclick={(e) => {
                                e.stopPropagation()
                                dropdownStatusOpen = !dropdownStatusOpen
                                if (dropdownStatusOpen && dropdownMethodOpen) dropdownMethodOpen = false
                            }}
                            class:no-sel={!sOptions.status}
                        >
                            <span>
                                {#if !sOptions.status}
                                    {$_('Status')}
                                {:else}
                                    {$resolve(statusText(sOptions.status))}
                                {/if}
                            </span>
                            {#if sOptions.status != ''}
                                <span
                                    class="clear-sel"
                                    aria-hidden="true"
                                    onclick={(e) => {
                                        e.stopPropagation()
                                        sOptions.status = ''
                                        setQueryString(sOptions)
                                    }}><Icon inline icon="fa:times" /></span
                                >
                            {/if}
                            <span class="caret"></span>
                        </button>
                        <ul class="dropdown-menu dropdown-menu-right" class:show={dropdownStatusOpen}>
                            {#each STATUSES as s}
                                {#if !TRANSIENT_STATUSES.has(s)}
                                    <li
                                        class="dd-menu-row"
                                        aria-hidden="true"
                                        class:active={sOptions.status == s}
                                        onclick={() => {
                                            sOptions.status = s
                                            dropdownStatusOpen = false
                                            setQueryString(sOptions)
                                        }}
                                    >
                                        <span>{$resolve(statusText(s))}</span>
                                    </li>
                                {/if}
                            {/each}
                            <li class="dd-divider"><span></span></li>
                            <li class="dd-group-label"><span>{$_('Transitoire')}</span></li>
                            {#each STATUSES as s}
                                {#if TRANSIENT_STATUSES.has(s)}
                                    <li
                                        class="dd-menu-row"
                                        aria-hidden="true"
                                        class:active={sOptions.status == s}
                                        onclick={() => {
                                            sOptions.status = s
                                            dropdownStatusOpen = false
                                            setQueryString(sOptions)
                                        }}
                                    >
                                        <span>{$resolve(statusText(s))}</span>
                                    </li>
                                {/if}
                            {/each}
                        </ul>
                    </div>
                </div>
                <div class="elf-method">
                    <div class="dropdown dropdown-selstyle dropdown-block">
                        <button
                            type="button"
                            class="btn btn-outline-secondary dropdown-toggle"
                            onclick={(e) => {
                                e.stopPropagation()
                                dropdownMethodOpen = !dropdownMethodOpen
                                if (dropdownStatusOpen && dropdownMethodOpen) dropdownStatusOpen = false
                            }}
                            class:no-sel={!sOptions.payment_method}
                        >
                            <span>
                                {#if !sOptions.payment_method}
                                    {$_('Méthode de paiment')}
                                {:else}
                                    {sOptions.payment_method}
                                {/if}
                            </span>
                            {#if sOptions.payment_method != ''}
                                <span
                                    class="clear-sel"
                                    aria-hidden="true"
                                    onclick={(e) => {
                                        e.stopPropagation()
                                        sOptions.payment_method = ''
                                        setQueryString(sOptions)
                                    }}><Icon inline icon="fa:times" /></span
                                >
                            {/if}
                            <span class="caret"></span>
                        </button>
                        <ul class="dropdown-menu dropdown-menu-right" class:show={dropdownMethodOpen}>
                            {#each METHODS as m}
                                <li
                                    class="dd-menu-row"
                                    aria-hidden="true"
                                    class:active={sOptions.payment_method == m}
                                    onclick={() => {
                                        sOptions.payment_method = m
                                        dropdownMethodOpen = false
                                        setQueryString(sOptions)
                                    }}
                                >
                                    <span>{$resolve(paymentMethodText(m))}</span>
                                </li>
                            {/each}
                        </ul>
                    </div>
                </div>
            </div>
        </form>
    </div>
</div>

{#if loading}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card">
        <div class="card-title text-primary">
            <span>{$_('Commandes')}</span>
            <span
                ><button
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    onclick={(e) => {
                        sOptions.s_by = sOptions.s_by == 'ctime' ? '' : 'ctime'
                        setQueryString(sOptions)
                    }}
                >
                    Tri :
                    {#if sOptions.s_by == 'ctime'}Création
                    {:else}Mise à jour
                    {/if}
                </button></span
            >
        </div>

        {#if $results.length}
            <div class="card-body">
                <table id="order-list" class="table mb-0" class:disabled-look={updating}>
                    <thead>
                        <tr>
                            <th>{$_('Summary')}</th>
                            <th>{$_('Owner')}</th>
                            <th></th>
                            <th>{$_('Status')}</th>
                            <th>
                                {$_('Création  (m-à-j)')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $results as item (item.id)}
                            <tr>
                                <td><a use:sanchor href={spath(`/ecom/order/${item.id}`)}>{item.text_desc}</a></td>
                                <td><a use:sanchor href={spath(`/wdom-user/${item.owner}`)}>{nameMapping[item.owner] ? nameMapping[item.owner] : item.owner}</a></td>
                                <td class="text-info"
                                    >{#if item.user_instructions}<span data-tooltip={item.user_instructions.substr(0, 40) + (item.user_instructions.length > 40 ? '…' : '')}><Icon inline icon="mdi:sticker-alert" /></span>{/if}</td
                                >
                                <td>{$resolve(statusText(item.status))}</td>
                                <td>
                                    <DateComponent value={item.ctime} format="h-table" />
                                    {#if !sameDate(item.ctime, item.mtime)}<span class="text-danger"><DateComponent value={item.mtime} format="h-table" /></span>{/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
        <div class="card-footer text-bg-light">
            <PageList
                loading={updating}
                {nItems}
                limit={LIMIT}
                skip={sOptions.skip}
                setSkip={(e) => {
                    sOptions.skip = e
                    setQueryString(sOptions)
                }}
            />
        </div>
    </div>
{/if}
